import { useReactFlow } from '@xyflow/react';
import React, { useMemo } from 'react';
import FormSelect from 'src/components/form-select';
import FormSwitch from 'src/components/form-switch';
import {
  IPoE,
  EDataType,
  NodeCategoryModel,
} from 'src/modules/network-configuration/models/node-data';
import { getInfoConnected } from 'src/utils/node';

export const defaultPoE: IPoE = {
  enable: false,
  fault: false,
  limit: 4,
  limit_pwr: 0,
  prio: 3,
};

const powerLimitOptions = [
  { label: 'Off', value: '0' },
  { label: '2W', value: '1' },
  { label: '4W', value: '2' },
  { label: '6W', value: '3' },
  { label: '8W', value: '4' },
  { label: '10W', value: '5' },
  { label: '12W', value: '6' },
  { label: '15W', value: '7' },
  { label: '18W', value: '8' },
  { label: '20W', value: '9' },
  { label: '24W', value: '10' },
];

const limitOptions = [
  { label: 'Class 0 (15.4W)', value: '0' },
  { label: 'Class 1 (4.3W)', value: '1' },
  { label: 'Class 2 (7W)', value: '2' },
  { label: 'Class 3 (15.4W)', value: '3' },
  { label: 'Class 4 (30W)', value: '4' },
];

interface IProps {
  id: string;
  values: {
    poe?: IPoE;
  };
  setValues: (value: React.SetStateAction<{ poe?: IPoE }>) => void;
}

export default function EditPoE({ id, values, setValues }: IProps) {
  const { getNodes, getEdges } = useReactFlow();
  const nodes = getNodes();
  const edges = getEdges();

  const poeValues = (values.poe || defaultPoE) as IPoE;

  const canEnablePoE = useMemo(() => {
    const currentNode = nodes.find((node) => node.id === id);
    const isDevice =
      (currentNode?.data?.category as NodeCategoryModel)?.type ===
      EDataType.Device;
    if (!isDevice) return false;
    const { port } = getInfoConnected(
      id,
      (currentNode?.data?.category as NodeCategoryModel)?.ports?.[0]?.id || '',
      edges,
      nodes,
    );
    if (!port) return true;

    return port.isPoE;
  }, [id, edges, nodes]);

  return (
    <div className="mt-2 grid grid-cols-2 gap-2">
      <div>
        <FormSwitch
          id="node-poe"
          name="nodePoE"
          label="Enable PoE"
          wrapperClassName="flex-col w-auto items-start justify-start gap-1"
          labelClassName="text-blue-400"
          className="focus:ring-blue-400 data-[checked]:bg-blue-400"
          checked={poeValues.enable}
          disabled={!canEnablePoE}
          data-testid={`poe-enable-${id}`}
          onChange={(_value) =>
            setValues((prev) => ({
              ...prev,
              poe: {
                ...(prev.poe || defaultPoE),
                enable: _value,
              },
            }))
          }
        />
      </div>
      <div>
        <FormSwitch
          id="poe-fault"
          name="poeFault"
          label="ITxPT xstatus"
          wrapperClassName="flex-col w-auto items-start justify-start gap-1"
          labelClassName="text-blue-400"
          className="focus:ring-blue-400 data-[checked]:bg-blue-400"
          checked={poeValues.fault}
          data-testid={`poe-fault-${id}`}
          onChange={(_value) =>
            setValues((prev) => ({
              ...prev,
              poe: {
                ...(prev.poe || defaultPoE),
                fault: _value,
              },
            }))
          }
        />
      </div>
      <div>
        <FormSelect
          label="Limit PoE Class"
          options={limitOptions}
          value={poeValues.limit.toString()}
          data-testid={`poe-limit-${id}`}
          onChange={(e) => {
            const limit = Number(e.target.value);
            setValues((prev) => ({
              ...prev,
              poe: {
                ...(prev.poe || defaultPoE),
                limit,
              },
            }));
          }}
        />
      </div>
      <div>
        <FormSelect
          label="Power Limit"
          options={powerLimitOptions}
          value={poeValues.limit_pwr.toString()}
          data-testid={`poe-limit-pwr-${id}`}
          onChange={(e) => {
            const limit_pwr = Number(e.target.value);
            setValues((prev) => ({
              ...prev,
              poe: {
                ...(prev.poe || defaultPoE),
                limit_pwr,
              },
            }));
          }}
        />
      </div>
      <div>
        <FormSelect
          id="poe-prio"
          label="Priority"
          name="poePrio"
          options={[
            { label: 'Low', value: '3' },
            { label: 'High', value: '2' },
            { label: 'Critical', value: '1' },
          ]}
          value={poeValues.prio}
          data-testid={`poe-prio-${id}`}
          onChange={(e) => {
            const prio = Number(e.target.value);
            setValues((prev) => ({
              ...prev,
              poe: {
                ...(prev.poe || defaultPoE),
                prio,
              },
            }));
          }}
        />
      </div>
    </div>
  );
}
